import { Bold, Copy, Italic, Pencil, Plus, Trash2 } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { api } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { BotonDescarga } from '@/components/BotonDescarga';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { OrdenableLista } from '@/components/OrdenableLista';
import { AvisoSinGuardar, PaginaEditor } from '@/components/PaginaEditor';
import { usePestana } from '@/hooks/usePestana';
import { envolver, marcadoAHtml } from '@/lib/marcado';
import { cn } from '@/lib/utils';

import { ArchivoUnico, type ValorArchivo } from '../comun/ArchivoUnico';
import { comunesDe, cuerpoComunes } from '../comun/comunes';
import { DatosComunes } from '../comun/DatosComunes';
import { LicenciasElemento } from '../comun/Paneles';
import { SelectorClase } from '../comun/SelectorClase';
import { BANDERAS, FONDO_INICIAL, FONDOS, TEXTO_INICIAL, TEXTOS } from './paleta';
import {
  useResumen,
  useResumenMutaciones,
  type Bloque,
  type BloqueCambio,
  type Resumen,
} from './queries';

type Mut = ReturnType<typeof useResumenMutaciones>;
type Tipo = Bloque['type'];

const PESTANAS = [
  ['bloques', 'Bloques'],
  ['general', 'Datos y archivos'],
  ['licencias', 'Licencias'],
] as const;

/** Un bloque como lo verá el alumno. */
function VistaBloque({ b }: { b: Bloque }) {
  const flags = BANDERAS.filter(([k]) => b[k]);
  return (
    <div className="space-y-1">
      {b.type === 'titulo' && (
        <div
          className="rounded-lg px-3 py-2 text-center font-extrabold"
          style={{
            background: FONDOS[b.bgColor ?? FONDO_INICIAL],
            color: TEXTOS[b.textColor ?? TEXTO_INICIAL],
          }}
        >
          {b.title}
        </div>
      )}
      {b.type === 'contenido' && (
        <p
          className="text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: marcadoAHtml(b.body ?? '') }}
        />
      )}
      {b.type === 'imagen' &&
        (b.image?.url ? (
          <img src={b.image.url} alt="" loading="lazy" className="max-h-72 rounded-lg" />
        ) : (
          <p className="text-sm text-texto-3">Imagen no disponible</p>
        ))}
      {flags.length > 0 && (
        <div className="flex gap-1">
          {flags.map(([k, corto, largo]) => (
            <span key={k} className="insignia" title={largo}>
              {corto}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Paleta({
  colores,
  valor,
  onCambio,
  etiqueta,
}: {
  colores: Record<string, string>;
  valor: string;
  onCambio: (v: string) => void;
  etiqueta: string;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-sm font-bold text-texto-2">{etiqueta}</span>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={etiqueta}>
        {Object.entries(colores).map(([k, hex]) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={valor === k}
            aria-label={`${etiqueta} ${hex}`}
            className={cn(
              'h-7 w-7 rounded-full border border-[var(--separador)]',
              valor === k && 'ring-2 ring-[var(--color-azul-600)] ring-offset-2',
            )}
            style={{ background: hex }}
            onClick={() => onCambio(k)}
          />
        ))}
      </div>
    </div>
  );
}

interface FormBloque {
  type: Tipo;
  title: string;
  body: string;
  bgColor: string;
  textColor: string;
  image: ValorArchivo | null;
  hasEvidence: boolean;
  hasRecommendation: boolean;
  hasGoodPractice: boolean;
  hasKeyRecommendation: boolean;
}

function ModalBloque({
  r,
  mut,
  bloque,
  afterId,
  onCerrar,
}: {
  r: Resumen;
  mut: Mut;
  bloque: Bloque | null;
  afterId: number | null;
  onCerrar: () => void;
}) {
  const [f, setF] = useState<FormBloque>({
    type: bloque?.type ?? 'contenido',
    title: bloque?.title ?? '',
    body: bloque?.body ?? '',
    bgColor: bloque?.bgColor ?? FONDO_INICIAL,
    textColor: bloque?.textColor ?? TEXTO_INICIAL,
    image: bloque?.image ?? null,
    hasEvidence: bloque?.hasEvidence ?? false,
    hasRecommendation: bloque?.hasRecommendation ?? false,
    hasGoodPractice: bloque?.hasGoodPractice ?? false,
    hasKeyRecommendation: bloque?.hasKeyRecommendation ?? false,
  });
  const area = useRef<HTMLTextAreaElement>(null);
  const poner = (c: Partial<FormBloque>) => setF((v) => ({ ...v, ...c }));
  const marcar = (m: string) => {
    const t = area.current;
    if (!t) return;
    const { valor, cursor } = envolver(f.body, t.selectionStart, t.selectionEnd, m);
    poner({ body: valor });
    requestAnimationFrame(() => {
      t.focus();
      t.setSelectionRange(cursor, cursor);
    });
  };
  const enviar = () => {
    const flags = {
      hasEvidence: f.hasEvidence,
      hasRecommendation: f.hasRecommendation,
      hasGoodPractice: f.hasGoodPractice,
      hasKeyRecommendation: f.hasKeyRecommendation,
    };
    const body =
      f.type === 'titulo'
        ? {
            title: f.title.trim(),
            bgColor: f.bgColor as NonNullable<BloqueCambio['bgColor']>,
            textColor: f.textColor as NonNullable<BloqueCambio['textColor']>,
          }
        : f.type === 'contenido'
          ? { body: f.body, ...flags }
          : { ...(f.image && { imagePath: f.image.path }), ...flags };
    const listo = { onSuccess: onCerrar };
    if (bloque) mut.editar.mutate({ childId: bloque.id, body: { type: f.type, ...body } }, listo);
    else mut.agregar.mutate({ type: f.type, afterId, ...body }, listo);
  };
  const vista: Bloque = {
    id: 0,
    sortOrder: 0,
    type: f.type,
    title: f.title,
    body: f.body,
    bgColor: f.bgColor,
    textColor: f.textColor,
    image: f.image,
    hasEvidence: f.hasEvidence,
    hasRecommendation: f.hasRecommendation,
    hasGoodPractice: f.hasGoodPractice,
    hasKeyRecommendation: f.hasKeyRecommendation,
  };
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={bloque ? 'Editar bloque' : 'Nuevo bloque'}
      ancho="max-w-2xl"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
      >
        {!bloque && (
          <div role="radiogroup" aria-label="Tipo de bloque" className="flex gap-2">
            {(
              [
                ['titulo', 'Título'],
                ['contenido', 'Contenido'],
                ['imagen', 'Imagen'],
              ] as const
            ).map(([k, t]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={f.type === k}
                className={cn('btn-cs', f.type === k ? 'btn-primario' : 'btn-vidrio')}
                onClick={() => poner({ type: k })}
              >
                {t}
              </button>
            ))}
          </div>
        )}
        {f.type === 'titulo' && (
          <>
            <Campo etiqueta="Título">
              <input
                className="input-vidrio"
                required
                maxLength={100}
                value={f.title}
                onChange={(e) => poner({ title: e.target.value })}
              />
            </Campo>
            <Paleta
              etiqueta="Color de fondo"
              colores={FONDOS}
              valor={f.bgColor}
              onCambio={(bgColor) => poner({ bgColor })}
            />
            <Paleta
              etiqueta="Color de texto"
              colores={TEXTOS}
              valor={f.textColor}
              onCambio={(textColor) => poner({ textColor })}
            />
          </>
        )}
        {f.type === 'contenido' && (
          <div>
            <div className="mb-1.5 flex items-center gap-1">
              <span className="mr-auto text-sm font-bold text-texto-2">Contenido</span>
              <button
                type="button"
                className="btn-cs btn-vidrio px-2 py-1"
                aria-label="Negrita"
                title="Negrita (**texto**)"
                onClick={() => marcar('**')}
              >
                <Bold size={14} aria-hidden />
              </button>
              <button
                type="button"
                className="btn-cs btn-vidrio px-2 py-1"
                aria-label="Cursiva"
                title="Cursiva (__texto__)"
                onClick={() => marcar('__')}
              >
                <Italic size={14} aria-hidden />
              </button>
            </div>
            <textarea
              ref={area}
              className="input-vidrio min-h-40 font-mono text-sm"
              aria-label="Contenido"
              required
              value={f.body}
              onChange={(e) => poner({ body: e.target.value })}
            />
            <p className="mt-1 text-xs text-texto-3">
              **texto** se ve en negritas y __texto__ en cursivas.
            </p>
          </div>
        )}
        {f.type === 'imagen' && (
          <ArchivoUnico
            destino="resumen-gpc"
            ownerId={r.id}
            tipos={['image']}
            valor={f.image}
            etiqueta="Imagen"
            onCambio={(image) => poner({ image })}
          />
        )}
        {f.type !== 'titulo' && (
          <div className="flex flex-wrap gap-4 text-sm">
            {BANDERAS.map(([k, corto, largo]) => (
              <label key={k} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--color-azul-600)]"
                  checked={f[k]}
                  onChange={(e) => poner({ [k]: e.target.checked })}
                />
                {corto} · {largo}
              </label>
            ))}
          </div>
        )}
        <section>
          <span className="mb-1.5 block text-sm font-bold text-texto-2">Vista previa</span>
          <div className="rounded-lg border border-[var(--separador)] p-3">
            <VistaBloque b={vista} />
          </div>
        </section>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={mut.agregar.isPending || mut.editar.isPending}
          >
            {bloque ? 'Guardar' : 'Agregar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Bloques({ r, mut }: { r: Resumen; mut: Mut }) {
  const editable = useCan('contenido', 'editar');
  const [modal, setModal] = useState<{ bloque: Bloque | null; afterId: number | null } | null>(
    null,
  );
  const [quitar, setQuitar] = useState<Bloque | null>(null);
  const insertar = (afterId: number | null): ReactNode =>
    editable && (
      <button
        type="button"
        className="flex w-full items-center justify-center gap-1 py-1 text-xs text-texto-2 hover:text-texto"
        onClick={() => setModal({ bloque: null, afterId })}
      >
        <Plus size={12} aria-hidden /> Insertar bloque aquí
      </button>
    );
  return (
    <div className="mx-auto max-w-3xl space-y-2">
      {editable && (
        <div className="flex justify-end">
          <button
            type="button"
            className="btn-cs btn-primario"
            onClick={() => setModal({ bloque: null, afterId: null })}
          >
            <Plus size={16} aria-hidden /> Agregar bloque al final
          </button>
        </div>
      )}
      {r.blocks.length === 0 && <Vacio titulo="Sin bloques" />}
      <OrdenableLista
        items={r.blocks}
        idDe={(b) => b.id}
        deshabilitado={!editable || mut.ordenar.isPending}
        onOrdenar={(ids) => mut.ordenar.mutate(ids)}
      >
        {(b, asa) => (
          <div>
            <div className="panel-vidrio flex items-start gap-2 p-3">
              {asa}
              <div className="min-w-0 flex-1">
                <VistaBloque b={b} />
              </div>
              {editable && (
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    className="btn-cs btn-vidrio px-2 py-1"
                    aria-label="Editar bloque"
                    onClick={() => setModal({ bloque: b, afterId: null })}
                  >
                    <Pencil size={14} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="btn-cs btn-vidrio px-2 py-1 text-rojo-texto"
                    aria-label="Borrar bloque"
                    onClick={() => setQuitar(b)}
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </div>
              )}
            </div>
            {insertar(b.id)}
          </div>
        )}
      </OrdenableLista>
      {modal && (
        <ModalBloque
          r={r}
          mut={mut}
          bloque={modal.bloque}
          afterId={modal.afterId}
          onCerrar={() => setModal(null)}
        />
      )}
      <ConfirmarAccion
        abierto={quitar !== null}
        onCerrar={() => setQuitar(null)}
        titulo="Borrar bloque"
        descripcion="Se quita del resumen."
        textoBoton="Borrar"
        peligro
        ocupado={mut.quitar.isPending}
        onConfirmar={() =>
          quitar && mut.quitar.mutate(quitar.id, { onSuccess: () => setQuitar(null) })
        }
      />
    </div>
  );
}

function General({ r, mut }: { r: Resumen; mut: Mut }) {
  const editable = useCan('contenido', 'editar');
  const inicial = {
    comunes: comunesDe(r),
    classId: r.classId,
    isUltra: r.isUltra,
    isPublished: r.isPublished,
    cover: r.cover,
    fullGuide: r.fullGuide,
    shortGuide: r.shortGuide,
  };
  const [f, setF] = useState(inicial);
  const sucio = JSON.stringify(f) !== JSON.stringify(inicial);
  const poner = (c: Partial<typeof f>) => setF((v) => ({ ...v, ...c }));
  return (
    <form
      className="grid gap-6 xl:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        mut.guardar.mutate(
          {
            ...cuerpoComunes(f.comunes),
            classId: f.classId,
            isUltra: f.isUltra,
            isPublished: f.isPublished,
            coverPath: f.cover?.path ?? null,
            fullGuidePath: f.fullGuide?.path ?? null,
            shortGuidePath: f.shortGuide?.path ?? null,
          },
          { onSuccess: () => toast.success('Resumen guardado.') },
        );
      }}
    >
      <AvisoSinGuardar sucio={sucio} />
      <section className="panel-vidrio space-y-4">
        <DatosComunes
          valor={f.comunes}
          onCambio={(c) => poner({ comunes: { ...f.comunes, ...c } })}
          nombreMax={250}
          deshabilitado={!editable}
        />
        <div>
          <span className="mb-1.5 block text-sm font-bold text-texto-2">Clase ligada</span>
          <SelectorClase
            valor={f.classId}
            deshabilitado={!editable}
            onCambio={(classId) => poner({ classId })}
          />
        </div>
        <fieldset disabled={!editable} className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              checked={f.isPublished}
              onChange={(e) => poner({ isPublished: e.target.checked })}
            />
            Disponible para el alumno
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              checked={f.isUltra}
              onChange={(e) => poner({ isUltra: e.target.checked })}
            />
            UltraResumen
          </label>
        </fieldset>
      </section>
      <section className="panel-vidrio space-y-4">
        <ArchivoUnico
          destino="resumen-gpc"
          ownerId={r.id}
          tipos={['image']}
          valor={f.cover}
          quitable
          deshabilitado={!editable}
          etiqueta="Portada"
          onCambio={(cover) => poner({ cover })}
        />
        <ArchivoUnico
          destino="resumen-gpc"
          ownerId={r.id}
          tipos={['pdf']}
          valor={f.fullGuide}
          quitable
          deshabilitado={!editable}
          etiqueta="Guía completa (PDF)"
          onCambio={(fullGuide) => poner({ fullGuide })}
        />
        <ArchivoUnico
          destino="resumen-gpc"
          ownerId={r.id}
          tipos={['pdf']}
          valor={f.shortGuide}
          quitable
          deshabilitado={!editable}
          etiqueta="Guía resumida (PDF)"
          onCambio={(shortGuide) => poner({ shortGuide })}
        />
      </section>
      {editable && (
        <div className="flex justify-end xl:col-span-2">
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={!sucio || mut.guardar.isPending}
          >
            Guardar
          </button>
        </div>
      )}
    </form>
  );
}

function Editor({ r }: { r: Resumen }) {
  const navigate = useNavigate();
  const editable = useCan('contenido', 'editar');
  const [pestana, setPestana] = usePestana(PESTANAS);
  const mut = useResumenMutaciones(r.id);
  const [borrando, setBorrando] = useState(false);
  const { proteger, dialogo } = useStepUp();
  return (
    <PaginaEditor
      volver={{ a: '/contenido/resumenes-gpc', texto: 'Resúmenes GPC' }}
      titulo={r.name}
      descripcion={`Resumen #${r.id}${r.code ? ` · ${r.code}` : ''} · ${r.isPublished ? 'disponible' : 'oculto'}${r.className ? ` · clase: ${r.className}` : ''}${r.createdByName ? ` · autor: ${r.createdByName}` : ''}`}
      acciones={
        <>
          <BotonDescarga
            etiqueta="PDF"
            nombre={`resumen-gpc-${r.id}.pdf`}
            pedir={() =>
              api.GET('/api/v1/admin/gpc-summaries/{id}/pdf', {
                params: { path: { id: String(r.id) } },
                parseAs: 'blob',
              })
            }
          />
          {editable && (
            <>
              <button
                type="button"
                className="btn-cs btn-vidrio"
                disabled={mut.duplicar.isPending}
                onClick={() =>
                  mut.duplicar.mutate(undefined, {
                    onSuccess: (d) => {
                      toast.success('Resumen duplicado (sin publicar).');
                      void navigate(`/contenido/resumenes-gpc/${d.id}`);
                    },
                  })
                }
              >
                <Copy size={16} aria-hidden /> Duplicar
              </button>
              <button type="button" className="btn-cs btn-vidrio" onClick={() => setBorrando(true)}>
                <Trash2 size={16} aria-hidden /> Borrar
              </button>
            </>
          )}
        </>
      }
      pestanas={PESTANAS}
      pestana={pestana}
      onPestana={setPestana}
    >
      {pestana === 'bloques' && <Bloques r={r} mut={mut} />}
      {pestana === 'general' && <General key={r.updatedAt} r={r} mut={mut} />}
      {pestana === 'licencias' && (
        <div className="panel-vidrio">
          <LicenciasElemento libraryItemId={r.libraryItemId} asignadas={r.membershipIds} />
        </div>
      )}
      <ConfirmarAccion
        abierto={borrando}
        onCerrar={() => setBorrando(false)}
        titulo={`Borrar «${r.name}»`}
        descripcion="Se borra con todos sus bloques y sale de las licencias. Los archivos del CDN no se borran."
        textoBoton="Borrar"
        peligro
        ocupado={mut.borrar.isPending}
        onConfirmar={() =>
          void proteger(() => mut.borrar.mutateAsync())
            .then(() => {
              toast.success('Resumen borrado.');
              void navigate('/contenido/resumenes-gpc');
            })
            .catch((e: unknown) => toast.error(mensajeDe(e)))
        }
      />
      {dialogo}
    </PaginaEditor>
  );
}

export function ResumenPage() {
  const id = Number(useParams().id);
  const q = useResumen(id);
  return (
    <PermissionGate seccion="contenido">
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && <Editor key={q.data.id} r={q.data} />}
    </PermissionGate>
  );
}
